import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback, useState } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Error, ErrorTypes } from 'sdk';

import { useUIState } from '../../app/App';
import { commonMessages } from '../../i18n/commonMessages';
import { styled } from '../../styled';
import { Theme } from '../../theme/interface/detailed';
import { Button } from '../buttons';
import { CloseIcon } from '../icons/closeIcon';
import { errorTypeMsgs } from './msgs';

const Component = observer(({ className }: { className: string }): ReactElement | null => {
	const { modalStore } = useUIState();
	const [error, setError] = useState<Error>();
	const pop = useCallback((): void => setError(modalStore.pop()), [modalStore]);
	const { formatMessage } = useIntl();

	const getType = useCallback(
		(type: ErrorTypes): string => {
			switch (type) {
				case ErrorTypes.Info:
					return formatMessage(errorTypeMsgs.info);
				case ErrorTypes.Warning:
					return formatMessage(errorTypeMsgs.warning);
				case ErrorTypes.Error:
					return formatMessage(errorTypeMsgs.error);
			}
		},
		[formatMessage]
	);

	useEffect(() => {
		if (!error && modalStore.len) {
			pop();
		}
	}, [modalStore.len, error, pop]);

	return error ? (
		<div className={className}>
			<CloseIcon className="close-icon" onClick={pop} />
			<div className="error-header">{getType(error.type)}</div>
			<div className="error-text">{error.message}</div>
			<div className="close-btn-container">
				<Button className="close-btn" onClick={pop}>
					{formatMessage(commonMessages.close)}
				</Button>
			</div>
		</div>
	) : null;
});

export const ModalNotification = styled(Component)(
	({
		theme: {
			allSettings: { modalNotifications },
		},
	}: {
		theme: Theme;
	}) => css`
		${modalNotifications.container};
		border-radius: 8px;
		box-shadow: 0 4px 8px rgba(34, 43, 85, 0.04), 0 16px 24px rgba(34, 43, 85, 0.1);
		left: calc(50% - 396px / 2);
		padding: 32px;
		position: fixed;
		top: 50%;
		transform: translateY(-50%);
		width: 330px;
		z-index: 100070;

		@media (max-width: 400px) {
			width: 200px;
			left: calc(50% - 264px / 2);
		}

		& .error-header {
			font-weight: bold;
			${modalNotifications.header};
		}

		& .close-icon {
			cursor: pointer;
			right: 21px;
			position: absolute;
			top: 21px;
		}

		& .error-text {
			${modalNotifications.errorText};
			padding: 16px 0;
		}

		& .close-btn-container {
			text-align: right;
			width: 100%;
		}

		& .close-btn {
			${modalNotifications.button};
			border-radius: 8px;
			cursor: pointer;
			display: inline-block;
			font-size: 14px;
			font-weight: bold;
			line-height: 16px;
			padding: 8px 16px;
		}
	`
);
